import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="onboarding"
export default class extends Controller {
  static targets = [
    "input",
    "professionInput",
    "planInput",
    "submitButton",
    "planForm",
    "avatarInput",
    "usernameInput",
  ];

  connect() {
    this.focusFirstInput();
    const progress = this.element.dataset.progress;
    this.element.style.setProperty("--progress-percentage", progress);
    this.validateProfileForm();
  }

  handleInput(event) {
    const currentInput = event.target;
    const currentIndex = this.inputTargets.indexOf(currentInput);

    const numericValue = currentInput.value.replace(/[^0-9]/g, "");
    currentInput.value = numericValue;

    if (numericValue && currentIndex < this.inputTargets.length - 1) {
      this.inputTargets[currentIndex + 1].focus();
    }

    if (this.isComplete()) {
      this.element.querySelector("form").requestSubmit();
    }
  }

  handleKeydown(event) {
    const currentInput = event.target;
    const currentIndex = this.inputTargets.indexOf(currentInput);

    if (event.key === "Backspace" && !currentInput.value && currentIndex > 0) {
      this.inputTargets[currentIndex - 1].focus();
    }
  }

  handlePaste(event) {
    event.preventDefault();
    const paste = event.clipboardData.getData("text");
    const digits = paste.match(/\d/g);

    if (digits) {
      digits.forEach((digit, i) => {
        if (i < this.inputTargets.length) {
          this.inputTargets[i].value = digit;
        }
      });

      if (this.isComplete()) {
        this.element.querySelector("form").requestSubmit();
      } else {
        const lastIndex = Math.min(digits.length, this.inputTargets.length) - 1;
        if (lastIndex >= 0) {
          this.inputTargets[lastIndex].focus();
        }
      }
    }
  }

  resetInputs() {
    this.inputTargets.forEach((input) => {
      input.value = "";
    });
    this.focusFirstInput();
  }

  isComplete() {
    return this.inputTargets.every((input) => /^\d$/.test(input.value));
  }

  focusFirstInput() {
    this.inputTargets[0]?.focus();
  }

  selectProfession(event) {
    const button = event.currentTarget;
    const profession = button.dataset.profession;

    this.professionInputTarget.value = profession;

    this.element.querySelectorAll("[data-profession]").forEach((btn) => {
      btn.classList.remove("border-indigo-500", "bg-indigo-50");
    });
    button.classList.add("border-indigo-500", "bg-indigo-50");

    this.submitButtonTarget.disabled = false;
  }

  handleFormSubmit(event) {
    if (!this.professionInputTarget.value) {
      event.preventDefault();
      return false;
    }
  }

  resetProfession() {
    this.professionInputTarget.value = "";
    this.submitButtonTarget.disabled = true;

    this.element.querySelectorAll("[data-profession]").forEach((btn) => {
      btn.classList.remove("border-indigo-500", "bg-indigo-50");
    });
  }

  selectPlan(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const plan = button.dataset.plan;

    this.planInputTarget.value = plan;

    this.element.querySelectorAll("[data-plan]").forEach((btn) => {
      btn.classList.remove("ring-2", "ring-indigo-500");
      btn.disabled = true;
    });
    button.classList.add("ring-2", "ring-indigo-500");

    if (plan === "free") {
      this.element
        .querySelector("form[action='/onboarding/update_plan']")
        .requestSubmit();
    } else {
      const form = this.element.querySelector(
        "form[action='/subscription_checkouts']"
      );
      form.setAttribute("data-turbo", "false");
      form.requestSubmit();
    }
  }

  handlePlanSubmit(event) {
    if (this.planInputTarget.value === "") {
      event.preventDefault();
    }
  }

  handlePlanSubmitStart() {
    this.element.querySelectorAll("[data-plan]").forEach((btn) => {
      btn.disabled = true;
    });
  }

  handlePlanSubmitEnd() {
    this.element.querySelectorAll("[data-plan]").forEach((btn) => {
      btn.disabled = false;
    });
  }

  resetPlan() {
    this.planInputTarget.value = "";
    this.element.querySelectorAll("[data-plan]").forEach((btn) => {
      btn.classList.remove("ring-2", "ring-indigo-500");
      btn.disabled = false;
    });
  }

  validateProfileForm() {
    const avatarSelected =
      this.hasAvatarInputTarget && this.avatarInputTarget.checked;
    const usernameValid =
      this.hasUsernameInputTarget && this.usernameInputTarget.value.length >= 3;

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = !(avatarSelected && usernameValid);
    }
  }

  handleAvatarSelection(event) {
    if (this.hasAvatarInputTarget) {
      this.avatarInputTarget.value = event.currentTarget.value;
      this.validateProfileForm();
    }
  }

  handleUsernameInput(event) {
    if (this.hasUsernameInputTarget) {
      this.validateProfileForm();
    }
  }
}
